// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import Alert from "src/components/shared/alerts/Alert/Alert";
import ProgressBar from "src/components/shared/displays/ProgressBar/ProgressBar";
import { WarningIcon } from "src/assets/icons";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirmDarkCta,
} from "src/styles/mui/muiStylesHelper";

// Types
import { UnassignedEMRType } from "src/services/emr/types";
import { PatientType } from "src/services/patients/types";

// Utils
import {
  buildBirthdateField,
  buildHealthCareIdField,
  buildNameField,
  buildSexField,
  getFullName,
  MatchFieldType,
} from "src/utils/emr/emrMatchHelper";

const HIGH_CONFIDENCE = 80;
const MEDIUM_CONFIDENCE = 50;

const CONFIDENCE_HIGH_COLOR = "#22BE39";
const CONFIDENCE_MEDIUM_COLOR = "#E7C43C";
const CONFIDENCE_LOW_COLOR = "#E10C0C";
const MISMATCH_COLOR = "#E10C0C";

const ROW_TEMPLATE = "minmax(0, 0.8fr) minmax(0, 1fr) minmax(0, 1fr)";
const ROW_BORDER = "1px solid #5B6D7A70";

// the matched name arrives on the job itself, every other record value needs
// the patient request to resolve first
type MatchRowType = MatchFieldType & { awaitsRecord: boolean };

type PatientMatchPanelType = {
  exam: UnassignedEMRType;
  matchedPatient: PatientType | undefined;
  isLoading: boolean;
  isError: boolean;
  onKeepMatched: () => void;
  onKeepExtracted: () => void;
};

const PatientMatchPanel = ({
  exam,
  matchedPatient,
  isLoading,
  isError,
  onKeepMatched,
  onKeepExtracted,
}: PatientMatchPanelType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // A distinct sunken dark surface, rather than inheriting the modal
  // body's own background -- and explicit light text/muted-text colors
  // instead of dimming near-white text with opacity, which reads poorly
  // against a medium-value background.
  const panelBackgroundColor = isDark ? "#202338" : undefined;
  const primaryTextColor = isDark ? "#FFF" : undefined;
  const mutedTextColor = isDark ? theme.palette.text.secondary : undefined;

  const matchScore = useMemo(
    () => Math.round((exam?.patientMatchScore ?? 0) * 100),
    [exam?.patientMatchScore]
  );

  const matchColor = useMemo(() => {
    if (matchScore >= HIGH_CONFIDENCE) return CONFIDENCE_HIGH_COLOR;
    if (matchScore >= MEDIUM_CONFIDENCE) return CONFIDENCE_MEDIUM_COLOR;
    return CONFIDENCE_LOW_COLOR;
  }, [matchScore]);

  const matchedName = useMemo(
    () =>
      getFullName(matchedPatient?.firstName, matchedPatient?.lastName) ||
      getFullName(
        exam?.matchedPatient?.firstName,
        exam?.matchedPatient?.lastName
      ),
    [
      matchedPatient?.firstName,
      matchedPatient?.lastName,
      exam?.matchedPatient?.firstName,
      exam?.matchedPatient?.lastName,
    ]
  );

  const rows: Array<MatchRowType> = useMemo(() => {
    const sexField = buildSexField(
      t("Sex"),
      exam?.patientSex,
      matchedPatient?.sex
    );

    return [
      {
        ...buildNameField(
          t("Name"),
          getFullName(exam?.patientFirstName, exam?.patientLastName),
          matchedName
        ),
        awaitsRecord: false,
      },
      {
        ...buildBirthdateField(
          t("Birthdate"),
          exam?.patientDateOfBirth,
          matchedPatient?.dateOfBirth
        ),
        awaitsRecord: true,
      },
      {
        ...buildHealthCareIdField(
          t("Healthcare Id"),
          exam?.patientHealthCareId,
          matchedPatient?.healthCareId
        ),
        awaitsRecord: true,
      },
      {
        ...sexField,
        // the canonical labels are translatable, an unrecognized value falls
        // back to itself
        fileValue: sexField.fileValue ? t(sexField.fileValue) : null,
        recordValue: sexField.recordValue ? t(sexField.recordValue) : null,
        awaitsRecord: true,
      },
    ];
  }, [
    t,
    exam?.patientFirstName,
    exam?.patientLastName,
    exam?.patientDateOfBirth,
    exam?.patientHealthCareId,
    exam?.patientSex,
    matchedPatient?.dateOfBirth,
    matchedPatient?.healthCareId,
    matchedPatient?.sex,
    matchedName,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        border: ROW_BORDER,
        borderRadius: "4px",
        backgroundColor: panelBackgroundColor,
        p: "12px",
        gap: "8px",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "14px", color: primaryTextColor }}>
          {t("Match Confidence")}
        </Typography>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 600, color: matchColor }}
        >
          {matchScore}%
        </Typography>
      </Box>

      <ProgressBar
        value={matchScore}
        displayValue={false}
        barColor={matchColor}
      />

      {isError && (
        <Alert variant="error" alertContent={t("Patient not found.")} />
      )}

      {!isError && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: ROW_TEMPLATE,
              columnGap: "8px",
              alignItems: "center",
            }}
          >
            <Box />
            <Typography
              sx={{
                fontSize: "12px",
                opacity: isDark ? 1 : 0.7,
                color: mutedTextColor,
              }}
            >
              {t("Extracted Record")}
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                opacity: isDark ? 1 : 0.7,
                color: mutedTextColor,
              }}
            >
              {t("Matched record")}
            </Typography>
          </Box>

          {rows.map((row) => (
            <Box
              key={row.label}
              sx={{
                display: "grid",
                gridTemplateColumns: ROW_TEMPLATE,
                columnGap: "8px",
                alignItems: "center",
                borderTop: ROW_BORDER,
                pt: "6px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  opacity: isDark ? 1 : 0.7,
                  color: mutedTextColor,
                }}
              >
                {row.label}
              </Typography>

              <Typography
                sx={{
                  fontSize: "13px",
                  wordBreak: "break-word",
                  color: primaryTextColor,
                }}
              >
                {row.fileValue ?? (
                  <Box
                    component="span"
                    sx={{ opacity: isDark ? 1 : 0.5, color: mutedTextColor }}
                  >
                    {t("Not in file")}
                  </Box>
                )}
              </Typography>

              {isLoading && row.awaitsRecord ? (
                <Skeleton variant="text" width="80%" />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: row.isMismatch ? MISMATCH_COLOR : "inherit",
                  }}
                >
                  {row.isMismatch && (
                    <Tooltip title={t("Does not match the imported file")}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "14px",
                        }}
                      >
                        <WarningIcon fontSize="inherit" />
                      </Box>
                    </Tooltip>
                  )}
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: row.isMismatch ? 600 : 400,
                      wordBreak: "break-word",
                    }}
                  >
                    {row.recordValue ?? "N/A"}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: ROW_TEMPLATE,
              columnGap: "8px",
              alignItems: "center",
            }}
          >
            <Box />
            {exam?.patientFirstName &&
            exam?.patientLastName &&
            exam?.patientDateOfBirth ? (
              <Button
                sx={[ModalCustomCancel, { minWidth: 0 }]}
                onClick={onKeepExtracted}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("Create from Extracted")}
                </Box>
              </Button>
            ) : (
              <Box />
            )}
            <Button
              sx={[ModalCustomConfirmDarkCta, { minWidth: 0 }]}
              onClick={onKeepMatched}
            >
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t("Keep Matched")}
              </Box>
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PatientMatchPanel;
